import { UkDateInput, ukDate } from "@/components/UkDateInput";
import { AgreementsOverview } from "@/components/agreements/AgreementsOverview";
import { RiskAssessment } from "@/components/RiskAssessment";
import { FileSignature as AgreementIcon } from "lucide-react";

const openAgreement = () => setTimeout(() => document.querySelector('[data-testid="client-agreements"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Search, Phone, MapPin, Trash2, Pencil, X, HeartPulse, AlertCircle, Cake,
  KeyRound, DoorOpen, Navigation, PoundSterling, ShieldCheck, Sparkles, TriangleAlert, Ban,
} from "lucide-react";
import { statusTone, HeaderAccent, CLIENT_TAGS, clientTag } from "@/lib/status";
import { SERVICE_NAMES } from "@/lib/services";
import { AREA_GROUPS } from "@/lib/areas";
import { FamilyRequestsAdmin } from "@/components/FamilyRequestsAdmin";
import { AgreementsAdmin } from "@/components/agreements/AgreementsAdmin";
import { useAuth } from "@/context/AuthContext";
import { AvailabilityEditor } from "@/components/AvailabilityEditor";
import { ClientFlags, ClientFlagsInputs } from "@/components/ClientFlags";

const EMPTY = {
  name: "", dob: "", address: "", phone: "", care_needs: "", medical_conditions: "",
  allergies: "", care_plan: "", status: "active", colour: "",
  key_safe_code: "", access_notes: "", latitude: "", longitude: "",
  billing_rate: 25, gdpr_consent: false, gdpr_notes: "", befriending_services: [],
  postcode: "", area: "", service_start_date: "", gdpr_consent_method: "", has_poa: false, poa_details: "",
  has_pets: false, pets_details: "", smoker: false, dnar: false, dnar_details: "",
  emergency_contact: { name: "", phone: "", relationship: "", email: "" },
  gp: { name: "", practice: "", phone: "", email: "", address: "" },
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [carers, setCarers] = useState([]);
  const { user: authUser } = useAuth();

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const loadReqs = (cid) => api.get(`/service-requests?client_id=${cid}`).then((r) => setReqs(r.data)).catch(() => setReqs([]));
  useEffect(() => {
    if (detail) {
      loadReqs(detail.id);
      api.get("/staff").then((r) => setCarers(r.data.filter((s) => s.role === "carer"))).catch(() => {});
    } else { setReqs([]); }
  }, [detail]);

  const reviewReq = async (req, status, addToProfile = false) => {
    try {
      await api.put(`/service-requests/${req.id}`, { status, add_to_profile: addToProfile });
      toast.success(status === "approved" ? "Service request approved" : "Service request declined");
      loadReqs(detail.id); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const deleteReq = async (req) => {
    if (!window.confirm(`Delete this "${req.service}" request?`)) return;
    try { await api.delete(`/service-requests/${req.id}`); toast.success("Request deleted"); loadReqs(detail.id); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c, latitude: c.latitude ?? "", longitude: c.longitude ?? "",
      emergency_contact: { ...EMPTY.emergency_contact, ...(c.emergency_contact || {}) }, gp: { ...EMPTY.gp, ...(c.gp || {}) } });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      billing_rate: parseFloat(form.billing_rate) || 0,
      latitude: form.latitude === "" || form.latitude === null ? null : parseFloat(form.latitude),
      longitude: form.longitude === "" || form.longitude === null ? null : parseFloat(form.longitude),
    };
    try {
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  const [tab, setTab] = useState("cards");
  const openClientAgreement = (id) => { const c = clients.find((x) => x.id === id); if (c) { setDetail(c); openAgreement(); } };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Clients</h1>
          <p className="mt-1 text-ink-soft">People we support — care plans, locations & billing.</p>
          <HeaderAccent />
        </div>
        <button onClick={openNew} data-testid="add-client-button"
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
          <Plus size={18} /> Add client
        </button>
      </div>

      <div className="flex gap-2" data-testid="clients-tabs">
        {[["cards", "Clients"], ["agreements", "Agreements overview"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`clients-tab-${k}`}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-ink text-white" : "bg-secondary text-ink hover:bg-brand-light"}`}>{l}</button>
        ))}
      </div>

      {tab === "agreements" && <AgreementsOverview key={detail ? "open" : "closed"} onOpen={openClientAgreement} canSend={["admin", "office"].includes(authUser?.role)} />}

      {tab === "cards" && <>
      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <input data-testid="client-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search clients..."
          className="w-full rounded-lg border border-border bg-white py-2.5 pl-10 pr-4 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.id}`}
            className={`group rounded-xl border border-border bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${clientTag(c.colour) ? `border-l-4 ${clientTag(c.colour).bar}` : ""}`}>
            <div className="flex items-start justify-between">
              <button onClick={() => setDetail(c)} className="text-left">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-light font-heading text-lg font-bold text-brand">
                  {c.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <h3 className="mt-3 font-heading text-lg font-semibold text-ink hover:text-brand">{c.name}</h3>
              </button>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${statusTone(c.status)}`}>
                {c.status}
              </span>
            </div>
            <div className="mt-4 space-y-2 text-sm text-ink-soft">
              {c.address && <p className="flex items-start gap-2"><MapPin size={15} className="mt-0.5 shrink-0 text-ink-muted" />{c.address}</p>}
              {c.phone && <p className="flex items-center gap-2"><Phone size={15} className="text-ink-muted" />{c.phone}</p>}
              {c.care_needs && <p className="flex items-start gap-2"><HeartPulse size={15} className="mt-0.5 shrink-0 text-ink-muted" />{c.care_needs}</p>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {clientTag(c.colour) && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-ink-soft"><span className={`h-2.5 w-2.5 rounded-full ${clientTag(c.colour).dot}`} />{clientTag(c.colour).label}</span>}
              {c.latitude != null && <span className="inline-flex items-center gap-1 rounded-full bg-slateblue-light px-2 py-0.5 text-xs font-medium text-slateblue"><Navigation size={11} /> Located</span>}
              {c.key_safe_code && <span className="inline-flex items-center gap-1 rounded-full bg-brand-light px-2 py-0.5 text-xs font-medium text-brand"><KeyRound size={11} /> Key safe</span>}
              {c.gdpr_consent && <span className="inline-flex items-center gap-1 rounded-full bg-sage-light px-2 py-0.5 text-xs font-medium text-sage-dark"><ShieldCheck size={11} /> GDPR</span>}
              {(c.risks || []).some((r) => r.level === "high") && <span data-testid={`high-risk-chip-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-coral-light px-2 py-0.5 text-xs font-semibold text-coral-dark"><TriangleAlert size={11} /> High risk</span>}
              <ClientFlags c={c} />
            </div>
            <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
              <button onClick={() => { setDetail(c); openAgreement(); }} data-testid={`client-agreement-button-${c.id}`}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark">
                <AgreementIcon size={14} /> Agreement
              </button>
              <button onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-sm font-medium text-ink transition-colors hover:bg-secondary">
                <Pencil size={14} /> Edit
              </button>
              <button onClick={() => remove(c)} data-testid={`delete-client-${c.id}`}
                className="flex items-center justify-center rounded-lg border border-border px-3 py-2 text-coral transition-colors hover:bg-coral-light">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="col-span-full py-12 text-center text-ink-muted">No clients found.</p>}
      </div>
      </>}

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetail(null)} />
          <div className="relative z-10 h-full w-full max-w-md overflow-y-auto bg-white p-8 shadow-xl scrollbar-thin">
            <button onClick={() => setDetail(null)} className="absolute right-5 top-5 text-ink-muted hover:text-ink"><X size={22} /></button>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-light font-heading text-2xl font-bold text-brand">
              {detail.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
            <h2 className="mt-4 font-heading text-2xl font-bold text-ink">{detail.name}</h2>
            <div className="mt-2"><ClientFlags c={detail} detailed /></div>
            <div className="mt-6 space-y-5 text-sm">
              {detail.dob && <Field icon={Cake} label="Date of birth" value={ukDate(detail.dob)} />}
              {detail.address && <Field icon={MapPin} label="Address" value={detail.address} />}
              {detail.phone && <Field icon={Phone} label="Phone" value={detail.phone} />}
              {detail.key_safe_code && <Field icon={KeyRound} label="Key safe code" value={detail.key_safe_code} />}
              {detail.access_notes && <Field icon={DoorOpen} label="Access notes" value={detail.access_notes} />}
              <Field icon={PoundSterling} label="Billing rate" value={`£${detail.billing_rate}/hr`} />
              <Field icon={HeartPulse} label="Care needs" value={detail.care_needs || "—"} />
              <Field icon={AlertCircle} label="Medical conditions" value={detail.medical_conditions || "—"} />
              <Field icon={AlertCircle} label="Allergies" value={detail.allergies || "None recorded"} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Care plan</p>
                <p className="mt-1 whitespace-pre-wrap text-ink">{detail.care_plan || "—"}</p>
              </div>
              <RiskAssessment key={detail.id} client={detail} onChange={load} />
              <ExclusionsSection client={detail} carers={carers} onChange={load} />
              <AvailabilityEditor kind="client" id={detail.id} initial={detail.availability} onSaved={load} />
              {detail.area && <p data-testid="client-detail-area" className="text-sm text-ink"><span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Area</span> · {detail.area}</p>}
              {detail.befriending_services?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Regular befriending services</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {detail.befriending_services.map((s) => <span key={s} className="rounded-full bg-brand-light px-2.5 py-1 text-xs font-medium text-brand">{s}</span>)}
                  </div>
                </div>
              )}
              <div data-testid="client-service-requests">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">
                  <Sparkles size={13} /> Service requests from family
                </p>
                {reqs.length === 0 ? (
                  <p className="mt-1.5 text-sm text-ink-muted">No service requests from the family yet.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {reqs.map((r) => (
                      <div key={r.id} data-testid={`service-request-${r.id}`} className={`rounded-lg border p-3 ${r.status === "pending" ? "border-gold/40 bg-gold-light/50" : r.status === "approved" ? "border-sage/40 bg-sage-light/50" : "border-border bg-secondary"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-ink">{r.service}</p>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${r.status === "pending" ? "bg-gold text-white" : r.status === "approved" ? "bg-sage text-white" : "bg-ink-muted text-white"}`}>{r.status}</span>
                            <button onClick={() => deleteReq(r)} data-testid={`delete-request-btn-${r.id}`} title="Delete request" className="text-ink-muted hover:text-coral"><Trash2 size={14} /></button>
                          </div>
                        </div>
                        {r.notes && <p className="mt-1 text-sm text-ink-soft">{r.notes}</p>}
                        <p className="mt-1 text-xs text-ink-muted">Requested by {r.requested_by}</p>
                        {r.status === "pending" && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button onClick={() => reviewReq(r, "approved", true)} data-testid={`approve-request-${r.id}`}
                              className="rounded-lg bg-sage px-3 py-1.5 text-xs font-semibold text-white hover:brightness-105">Approve & add to plan</button>
                            <button onClick={() => reviewReq(r, "approved", false)} data-testid={`approve-only-request-${r.id}`}
                              className="rounded-lg border border-sage px-3 py-1.5 text-xs font-semibold text-sage-dark hover:bg-sage-light">Approve only</button>
                            <button onClick={() => reviewReq(r, "declined")} data-testid={`decline-request-${r.id}`}
                              className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-coral hover:bg-coral-light">Decline</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <AgreementsAdmin client={detail} carers={carers} canManage={["admin", "office"].includes(authUser?.role)} isAdmin={authUser?.role === "admin"} />
              <FamilyRequestsAdmin clientId={detail.id} carers={carers} canManage={["admin", "office"].includes(authUser?.role)} />
              <div className={`rounded-lg p-4 ${detail.gdpr_consent ? "bg-sage-light" : "bg-gold-light"}`}>
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-soft"><ShieldCheck size={13} /> GDPR consent: {detail.gdpr_consent ? "Given" : "Not recorded"}</p>
                {detail.gdpr_notes && <p className="mt-1 text-ink">{detail.gdpr_notes}</p>}
              </div>
              <div className="rounded-lg bg-brand-light/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-brand">Emergency contact</p>
                <p className="mt-1 font-medium text-ink">{detail.emergency_contact?.name || "—"} · {detail.emergency_contact?.relationship}</p>
                <p className="text-ink-soft">{detail.emergency_contact?.phone}</p>
                {detail.emergency_contact?.email && <p className="text-ink-soft" data-testid="client-nok-email-display">{detail.emergency_contact.email}</p>}
              </div>
              <div className="rounded-lg bg-slateblue-light/60 p-4" data-testid="client-gp-details">
                <p className="text-xs font-semibold uppercase tracking-wider text-slateblue">GP details</p>
                {detail.gp && Object.values(detail.gp).some(Boolean) ? (
                  <>
                    <p className="mt-1 font-medium text-ink">{[detail.gp.name, detail.gp.practice].filter(Boolean).join(" · ") || "—"}</p>
                    {detail.gp.phone && <p className="text-ink-soft">{detail.gp.phone}</p>}
                    {detail.gp.email && <p className="text-ink-soft">{detail.gp.email}</p>}
                    {detail.gp.address && <p className="text-ink-soft">{detail.gp.address}</p>}
                  </>
                ) : <p className="mt-1 text-ink-muted">Not recorded — add them with Edit, or the family can add them in their portal.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {open && <ClientForm form={form} setForm={setForm} onClose={() => setOpen(false)} onSave={save} editing={editing} />}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="mt-0.5 shrink-0 text-ink-muted" />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{label}</p>
        <p className="text-ink">{value}</p>
      </div>
    </div>
  );
}

const RISK_PRESETS = ["Falls", "Risk of injury", "Diabetes", "Medication", "Choking / swallowing", "Pressure sores", "Mobility", "Lone working", "Behavioural", "Fire / home safety"];

function _LegacyRiskSection({ client, onChange }) {
  const [risks, setRisks] = useState(client.risks || []);
  const [adding, setAdding] = useState(false);
  const EMPTY_R = { category: "", level: "medium", description: "", controls: "", review_date: "" };
  const [f, setF] = useState(EMPTY_R);
  const input = "w-full rounded-lg border border-border bg-white px-2.5 py-1.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const badge = (lvl) => lvl === "high" ? "bg-coral text-white" : lvl === "medium" ? "bg-gold text-white" : "bg-sage text-white";

  const add = async () => {
    if (!f.category.trim()) { toast.error("Name the risk (e.g. Falls)"); return; }
    try {
      const { data } = await api.post(`/clients/${client.id}/risks`, f);
      setRisks((r) => [...r, data]); setF(EMPTY_R); setAdding(false); toast.success("Risk added"); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/clients/${client.id}/risks/${id}`); setRisks((r) => r.filter((x) => x.id !== id)); toast.success("Risk removed"); onChange && onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="risk-assessment-section">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-coral"><TriangleAlert size={13} /> Risk assessment</p>
        <button onClick={() => setAdding((v) => !v)} data-testid="add-risk-toggle" className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"><Plus size={13} /> Add risk</button>
      </div>

      {risks.length === 0 && !adding && <p className="mt-1.5 text-sm text-ink-muted">No risks recorded yet.</p>}

      <div className="mt-2 space-y-2">
        {risks.map((r) => (
          <div key={r.id} data-testid={`risk-${r.id}`} className={`rounded-lg border p-3 ${r.level === "high" ? "border-coral/40 bg-coral-light/40" : r.level === "medium" ? "border-gold/40 bg-gold-light/40" : "border-sage/40 bg-sage-light/40"}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${badge(r.level)}`}>{r.level}</span>
                <span className="text-sm font-semibold text-ink">{r.category}</span>
              </div>
              <button onClick={() => del(r.id)} data-testid={`delete-risk-${r.id}`} className="text-ink-muted hover:text-coral"><Trash2 size={13} /></button>
            </div>
            {r.description && <p className="mt-1 text-sm text-ink-soft">{r.description}</p>}
            {r.controls && <p className="mt-1 text-xs text-ink-muted"><b>Controls:</b> {r.controls}</p>}
            {r.review_date && <p className="mt-1 text-xs text-ink-muted">Review by {ukDate(r.review_date)}</p>}
          </div>
        ))}
      </div>

      {adding && (
        <div className="mt-2 space-y-2 rounded-lg border border-border bg-ivory/60 p-3" data-testid="risk-form">
          <div className="flex flex-wrap gap-1.5">
            {RISK_PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setF((x) => ({ ...x, category: p }))}
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${f.category === p ? "bg-brand text-white" : "bg-white text-ink-soft hover:bg-secondary"}`}>{p}</button>
            ))}
          </div>
          <input data-testid="risk-category-input" className={input} placeholder="Risk (e.g. Falls, Diabetes)" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
          <div className="flex gap-1.5" data-testid="risk-level-picker">
            {["low", "medium", "high"].map((l) => (
              <button key={l} type="button" onClick={() => setF({ ...f, level: l })}
                className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold capitalize ${f.level === l ? badge(l) : "bg-white text-ink-soft border border-border"}`}>{l}</button>
            ))}
          </div>
          <textarea rows={2} className={input} placeholder="Description of the risk" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <textarea rows={2} className={input} placeholder="Controls / how we keep them safe" value={f.controls} onChange={(e) => setF({ ...f, controls: e.target.value })} />
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">Review by</label>
            <UkDateInput value={f.review_date} onChange={(v) => setF({ ...f, review_date: v })} testid="client-review-input" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setAdding(false); setF(EMPTY_R); }} className="flex-1 rounded-lg border border-border py-1.5 text-sm font-medium text-ink hover:bg-secondary">Cancel</button>
            <button onClick={add} data-testid="save-risk-button" className="flex-1 rounded-lg bg-brand py-1.5 text-sm font-semibold text-white hover:bg-brand-dark">Save risk</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientForm({ form, setForm, onClose, onSave, editing }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEc = (k, v) => setForm((f) => ({ ...f, emergency_contact: { ...f.emergency_contact, [k]: v } }));
  const setGp = (k, v) => setForm((f) => ({ ...f, gp: { ...(f.gp || {}), [k]: v } }));
  const [geo, setGeo] = useState("");
  const findFromAddress = async () => {
    if (!form.address && !form.postcode) { toast.error("Enter the full address and postcode first"); return; }
    setGeo("Finding…");
    try {
      const { data } = await api.post("/clients/geocode", { address: form.address, postcode: form.postcode });
      setForm((f) => ({ ...f, latitude: String(data.latitude), longitude: String(data.longitude) }));
      setGeo(data.precision === "postcode" ? `Found the postcode area only: ${data.matched}. For the exact door, use "Use my current location" at the house.` : `Found: ${data.matched}`);
      toast.success("Location found from the address");
    } catch (e) { setGeo(""); toast.error(apiError(e)); }
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device."); return; }
    toast.message("Getting your location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })); toast.success("Home location pinned from your current position"); },
      () => toast.error("Couldn't get your location — allow location access and try again."),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={onSave} data-testid="client-form"
        className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl scrollbar-thin">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">{editing ? "Edit client" : "Add client"}</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <L label="Full name" req><input data-testid="client-name-input" required className={input} value={form.name} onChange={(e) => set("name", e.target.value)} /></L>
          <L label="Date of birth"><UkDateInput dob value={form.dob} max={new Date().toISOString().slice(0, 10)} onChange={(v) => set("dob", v)} testid="client-dob-input" /></L>
          <L label="Phone"><input className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></L>
          <L label="Status">
            <select className={input} value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </select>
          </L>
          <L label="Full address (house number, street, town)" full><input data-testid="client-address-input" className={input} placeholder="e.g. 12 Main Street, Paisley" value={form.address} onChange={(e) => set("address", e.target.value)} /></L>
          <L label="Postcode"><input data-testid="client-postcode-input" className={input} placeholder="e.g. BS1 4ST" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} /></L>
          <L label="Area">
            <select data-testid="client-area-select" className={input} value={form.area || ""} onChange={(e) => set("area", e.target.value)}>
              <option value="">Select area…</option>
              {AREA_GROUPS.map(([g, towns]) => <optgroup key={g} label={g}>{towns.map((t) => <option key={t} value={t}>{t}</option>)}</optgroup>)}
            </select>
          </L>
          <L label="Service start date"><UkDateInput testid="client-start-input" value={form.service_start_date} onChange={(v) => set("service_start_date", v)} /></L>
          <L label="Care needs" full><textarea rows={2} className={input} value={form.care_needs} onChange={(e) => set("care_needs", e.target.value)} /></L>
          <L label="Medical conditions"><input className={input} value={form.medical_conditions} onChange={(e) => set("medical_conditions", e.target.value)} /></L>
          <L label="Allergies"><input className={input} value={form.allergies} onChange={(e) => set("allergies", e.target.value)} /></L>
          <L label="Care plan" full><textarea rows={3} className={input} value={form.care_plan} onChange={(e) => set("care_plan", e.target.value)} /></L>
          <ClientFlagsInputs form={form} set={set} input={input} />
          <L label="Regular befriending services" full>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="client-services">
              {SERVICE_NAMES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={(form.befriending_services || []).includes(s)}
                    onChange={(e) => set("befriending_services", e.target.checked ? [...(form.befriending_services || []), s] : (form.befriending_services || []).filter((x) => x !== s))} className="accent-brand" />
                  {s}
                </label>
              ))}
            </div>
          </L>
        </div>

        <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Colour tag</p>
        <div className="flex flex-wrap items-center gap-2" data-testid="client-colour-picker">
          <button type="button" onClick={() => set("colour", "")} title="No colour"
            className={`flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white text-xs text-ink-muted ${!form.colour ? "border-ink" : "border-border"}`}>–</button>
          {Object.entries(CLIENT_TAGS).map(([key, t]) => (
            <button key={key} type="button" data-testid={`colour-${key}`} onClick={() => set("colour", key)} title={t.label}
              className={`h-8 w-8 rounded-full ${t.dot} ring-2 ring-offset-2 transition-all ${form.colour === key ? "ring-ink" : "ring-transparent"}`} />
          ))}
        </div>

        <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Location & entry</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <L label="Key safe code"><input data-testid="client-keysafe-input" className={input} placeholder="e.g. 1234" value={form.key_safe_code} onChange={(e) => set("key_safe_code", e.target.value)} /></L>
          <L label="Access notes"><input className={input} placeholder="e.g. key safe by back door" value={form.access_notes} onChange={(e) => set("access_notes", e.target.value)} /></L>
          <L label="Latitude"><input type="number" step="any" className={input} placeholder="51.4595" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} /></L>
          <L label="Longitude"><input type="number" step="any" className={input} placeholder="-2.5879" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} /></L>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" data-testid="find-location-button" onClick={findFromAddress}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark">
            <MapPin size={15} /> Find location from address
          </button>
          <button type="button" data-testid="use-my-location-button" onClick={useMyLocation}
            className="inline-flex items-center gap-2 rounded-lg border border-brand bg-brand/5 px-3.5 py-2 text-sm font-medium text-brand transition-colors hover:bg-brand/10">
            <Navigation size={15} /> Use my current location
          </button>
        </div>
        {geo && <p className="mt-1.5 text-xs text-sage-dark" data-testid="geocode-result">{geo}</p>}
        <p className="mt-1.5 text-xs text-ink-muted">Type the full address and postcode above, then tap "Find location from address" — the location is also found automatically when you save. For the most exact pin, stand at the front door and tap "Use my current location".</p>

        <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Billing & GDPR</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <L label="Billing rate (£/hr)"><input type="number" step="0.5" data-testid="client-billing-input" className={input} value={form.billing_rate} onChange={(e) => set("billing_rate", e.target.value)} /></L>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" id="gdpr" data-testid="client-gdpr-checkbox" checked={form.gdpr_consent} onChange={(e) => set("gdpr_consent", e.target.checked)} className="h-4 w-4 accent-brand" />
            <label htmlFor="gdpr" className="text-sm font-medium text-ink">GDPR consent obtained</label>
          </div>
          <L label="GDPR notes" full><input className={input} placeholder="Consent details / data-sharing preferences" value={form.gdpr_notes} onChange={(e) => set("gdpr_notes", e.target.value)} /></L>
          <L label="How was GDPR consent received?">
            <select data-testid="client-gdpr-method" className={input} value={form.gdpr_consent_method} onChange={(e) => set("gdpr_consent_method", e.target.value)}>
              <option value="">Not recorded</option>
              <option value="phone">By phone</option>
              <option value="email">By email</option>
              <option value="verbal">Verbal (in person)</option>
              <option value="written">Written / signed</option>
            </select>
          </L>
          <div className="flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" id="poa" data-testid="client-poa-checkbox" checked={form.has_poa} onChange={(e) => set("has_poa", e.target.checked)} className="h-4 w-4 accent-brand" />
            <label htmlFor="poa" className="text-sm font-medium text-ink">Power of Attorney / Guardianship in place</label>
          </div>
          {form.has_poa && <L label="POA / Guardianship details" full><input data-testid="client-poa-details" className={input} placeholder="Attorney/guardian name, type (welfare/financial), contact" value={form.poa_details} onChange={(e) => set("poa_details", e.target.value)} /></L>}
        </div>

        <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Emergency contact / next of kin</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input placeholder="Name" className={input} value={form.emergency_contact.name} onChange={(e) => setEc("name", e.target.value)} />
          <input placeholder="Relationship" className={input} value={form.emergency_contact.relationship} onChange={(e) => setEc("relationship", e.target.value)} />
          <input placeholder="Phone" className={input} value={form.emergency_contact.phone} onChange={(e) => setEc("phone", e.target.value)} />
          <input type="email" placeholder="Email (for agreements)" data-testid="client-nok-email" className={input} value={form.emergency_contact.email || ""} onChange={(e) => setEc("email", e.target.value)} />
        </div>

        <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">GP details</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input placeholder="GP name" data-testid="client-gp-name" className={input} value={form.gp?.name || ""} onChange={(e) => setGp("name", e.target.value)} />
          <input placeholder="Practice / surgery" data-testid="client-gp-practice" className={input} value={form.gp?.practice || ""} onChange={(e) => setGp("practice", e.target.value)} />
          <input placeholder="Phone" data-testid="client-gp-phone" className={input} value={form.gp?.phone || ""} onChange={(e) => setGp("phone", e.target.value)} />
          <input placeholder="Email (optional)" data-testid="client-gp-email" className={input} value={form.gp?.email || ""} onChange={(e) => setGp("email", e.target.value)} />
          <input placeholder="Practice address" data-testid="client-gp-address" className={`${input} sm:col-span-2`} value={form.gp?.address || ""} onChange={(e) => setGp("address", e.target.value)} />
        </div>
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button type="submit" data-testid="save-client-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Save</button>
        </div>
      </form>
    </div>
  );
}

function ExclusionsSection({ client, carers, onChange }) {
  const [sel, setSel] = useState(client.excluded_carers || []);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/clients/${client.id}/exclusions`, { carer_ids: sel }); toast.success("Pairing preferences saved"); onChange && onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="exclusions-section">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Ban size={13} /> Befriender pairing — do not send</p>
      <p className="mt-1 text-xs text-ink-muted">Tick any befriender this client (or the befriender) has asked not to be paired with. They'll be blocked from scheduling and check-in.</p>
      <div className="mt-2 space-y-1">
        {carers.length === 0 && <p className="text-sm text-ink-muted">No befrienders found.</p>}
        {carers.map((c) => (
          <label key={c.id} data-testid={`exclude-carer-${c.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-secondary">
            <input type="checkbox" className="h-4 w-4 accent-coral" checked={sel.includes(c.id)} onChange={() => toggle(c.id)} />
            <span className={sel.includes(c.id) ? "font-medium text-coral-dark" : "text-ink"}>{c.name}</span>
          </label>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="save-exclusions-button"
        className="mt-2 rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{saving ? "Saving…" : "Save pairing preferences"}</button>
    </div>
  );
}

function L({ label, children, full, req }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium text-ink">{label}{req && <span className="text-coral"> *</span>}</label>
      {children}
    </div>
  );
}
